import Image from "next/image";

import cn from "classnames";

import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { urlFor } from "@/sanity/utils";

import type {
  SmbHorizontalCardsCardData,
  SmbHorizontalCardsVariant,
} from "./smbHorizontalCards.types";

const IMAGE_WIDTH = 800;

export function SmbHorizontalCardsCard({
  card,
  variant,
}: SmbHorizontalCardsCardProps) {
  const imageUrl =
    variant !== "textCarousel" && card.image?.asset
      ? urlFor(card.image).width(IMAGE_WIDTH).url()
      : null;
  const alt = card.image?.alt ?? "";
  const title = (
    <Text variant="h6" component="h3" className="text-greyscale-headline">
      {card.title}
    </Text>
  );
  const body = card.body?.length ? (
    <div className="text-greyscale-body [&_a]:text-secondary-100">
      <RichText>{card.body}</RichText>
    </div>
  ) : null;

  if (variant === "productTiles") {
    // Mobile: the whole card is gray with the title inside. Desktop: only the
    // image tile is gray and the title sits below it on the section background.
    return (
      <article
        data-testid="smb-horizontal-cards-card"
        className="bg-greyscale-background flex h-full flex-col rounded-2xl lg:bg-transparent">
        <div className="bg-greyscale-background relative aspect-square w-full overflow-hidden rounded-2xl">
          {imageUrl ? (
            <Image
              src={imageUrl}
              alt={alt}
              fill
              sizes="(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw"
              className="object-contain p-6"
            />
          ) : null}
        </div>
        <div className="px-6 pb-6 lg:px-0 lg:pt-4 lg:pb-0">{title}</div>
      </article>
    );
  }

  return (
    <article
      data-testid="smb-horizontal-cards-card"
      className={cn(
        "flex h-full flex-col overflow-hidden rounded-2xl",
        variant === "textCarousel" ? "bg-white" : "bg-greyscale-background",
      )}>
      {imageUrl ? (
        <div className="relative aspect-4/3 w-full">
          <Image
            src={imageUrl}
            alt={alt}
            fill
            sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
            className="object-cover"
          />
        </div>
      ) : null}
      <div className="flex flex-1 flex-col gap-2 p-6">
        {title}
        {body}
      </div>
    </article>
  );
}

export type SmbHorizontalCardsCardProps = {
  card: SmbHorizontalCardsCardData;
  variant: SmbHorizontalCardsVariant;
};
